import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ChartLineUp, ArrowClockwise, User, MapTrifold, Phone,
  ArrowUpRight,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Legend,
} from "recharts";

const RANGES = [
  { v: 7, l: "Last 7 days" },
  { v: 14, l: "Last 14 days" },
  { v: 30, l: "Last 30 days" },
];

export default function PhoneReport() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/phone/report?days=${d}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <AppShell
      title="Phone Analytics"
      subtitle="Missed vs attended calls — per staff and per LSGD"
      actions={
        <div className="flex gap-2 flex-wrap">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="phrpt-range"
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
            {RANGES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          <button onClick={() => load(days)} data-testid="phrpt-refresh"
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <ArrowClockwise size={12}/>Refresh
          </button>
          <Link to="/crm/phone" data-testid="phrpt-back"
                className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <Phone size={12}/>Call feed
          </Link>
        </div>
      }
    >
      {loading && <div className="text-sm text-[#8A8780]">Loading report…</div>}
      {!loading && data && (
        <div className="space-y-4">
          {/* Headline metrics */}
          <div className="grid grid-cols-3 gap-3" data-testid="phrpt-headline">
            <MetricCard label="Response rate" value={`${data.response_rate}%`}
                          tone="blue" testid="phrpt-response-rate"/>
            <MetricCard label="Attended" value={data.total_attended}
                          tone="green" testid="phrpt-attended"/>
            <MetricCard label="Missed" value={data.total_missed}
                          tone="red" testid="phrpt-missed"/>
          </div>

          {/* Daily trend */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-4"
                data-testid="phrpt-trend">
            <div className="flex items-center gap-2 mb-2">
              <ChartLineUp size={16} className="text-[#0B2B66]"/>
              <div className="font-heading text-sm">Daily activity</div>
              <div className="text-[10px] text-[#8A8780] ml-2">Missed / attended / outgoing</div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.series} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9"/>
                  <XAxis dataKey="day" fontSize={10} tick={{ fill: "#5C5A55" }}
                          tickFormatter={(d) => d.slice(5)}/>
                  <YAxis fontSize={10} tick={{ fill: "#5C5A55" }} allowDecimals={false}/>
                  <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #E5E2D9",
                                             fontSize: 11, borderRadius: 6 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="missed" fill="#C25D38" name="Missed"/>
                  <Bar dataKey="attended" fill="#2D5A27" name="Attended"/>
                  <Bar dataKey="outgoing" fill="#0B2B66" name="Outgoing"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-staff table */}
          <div className="bg-white border border-[#E5E2D9] rounded-md"
                data-testid="phrpt-by-staff">
            <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
              <User size={14} className="text-[#2D5A27]"/>
              <div className="font-heading text-sm">By staff</div>
              <div className="text-[10px] text-[#8A8780] ml-auto">{data.by_staff.length} staff</div>
            </div>
            <StaffTable rows={data.by_staff}/>
          </div>

          {/* Per-LSGD table */}
          <div className="bg-white border border-[#E5E2D9] rounded-md"
                data-testid="phrpt-by-lsgd">
            <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
              <MapTrifold size={14} className="text-[#2D5A27]"/>
              <div className="font-heading text-sm">By LSGD</div>
              <div className="text-[10px] text-[#8A8780] ml-auto">{data.by_lsgd.length} LSGDs</div>
            </div>
            <LsgdTable rows={data.by_lsgd}/>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function MetricCard({ label, value, tone, testid }) {
  const color = tone === "blue" ? "text-[#0B2B66]"
             : tone === "green" ? "text-[#1A3317]"
             : tone === "red" ? "text-[#7A2E18]" : "text-[#3A3A38]";
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${color}`}>{value ?? "—"}</div>
    </div>
  );
}

function StaffTable({ rows }) {
  if (!rows.length) return <div className="p-4 text-xs text-[#8A8780]">No calls in this range.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] bg-[#F7F6F2]">
          <tr>
            <th className="text-left px-4 py-2">Staff</th>
            <th className="text-right px-3 py-2">Total</th>
            <th className="text-right px-3 py-2">Incoming</th>
            <th className="text-right px-3 py-2">Attended</th>
            <th className="text-right px-3 py-2">Missed</th>
            <th className="text-right px-3 py-2">Outgoing</th>
            <th className="text-right px-3 py-2">Callback</th>
            <th className="text-right px-3 py-2">Attend %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const denom = r.incoming;
            const pct = denom ? Math.round((r.attended / denom) * 100) : 0;
            return (
              <tr key={r.staff_id} className="border-t border-[#F0EDE4]"
                  data-testid={`phrpt-staff-${r.staff_id}`}>
                <td className="px-4 py-2 font-medium">{r.staff_name}</td>
                <td className="px-3 py-2 font-mono text-right">{r.total}</td>
                <td className="px-3 py-2 font-mono text-right">{r.incoming}</td>
                <td className="px-3 py-2 font-mono text-right text-[#1A3317]">{r.attended}</td>
                <td className="px-3 py-2 font-mono text-right text-[#7A2E18]">{r.missed}</td>
                <td className="px-3 py-2 font-mono text-right text-[#0B2B66]">{r.outgoing}</td>
                <td className="px-3 py-2 font-mono text-right text-[#7A5A11]">{r.callback_pending}</td>
                <td className={`px-3 py-2 font-mono text-right font-semibold ${
                  pct >= 80 ? "text-[#1A3317]" : pct >= 50 ? "text-[#7A5A11]" : "text-[#7A2E18]"
                }`}>{pct}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LsgdTable({ rows }) {
  if (!rows.length) return <div className="p-4 text-xs text-[#8A8780]">No calls in this range.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] bg-[#F7F6F2]">
          <tr>
            <th className="text-left px-4 py-2">LSGD</th>
            <th className="text-right px-3 py-2">Total</th>
            <th className="text-right px-3 py-2">Incoming</th>
            <th className="text-right px-3 py-2">Attended</th>
            <th className="text-right px-3 py-2">Missed</th>
            <th className="text-right px-3 py-2">Outgoing</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.lsgd_id} className="border-t border-[#F0EDE4]"
                data-testid={`phrpt-lsgd-${r.lsgd_id}`}>
              <td className="px-4 py-2 font-medium">{r.lsgd_name}</td>
              <td className="px-3 py-2 font-mono text-right">{r.total}</td>
              <td className="px-3 py-2 font-mono text-right">{r.incoming}</td>
              <td className="px-3 py-2 font-mono text-right text-[#1A3317]">{r.attended}</td>
              <td className="px-3 py-2 font-mono text-right text-[#7A2E18]">{r.missed}</td>
              <td className="px-3 py-2 font-mono text-right text-[#0B2B66]">{r.outgoing}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
